import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AlertPills from "../Utils/AlertPills";
import {
   getPendingVideoSends,
   getRecommendedVideos,
   sendRecommendedVideo,
} from "../../api/recommendedVideosService";
import { closePopup } from "../Common/popupUIStore";

function readPendingReservationId(item) {
   if (item == null) return "";
   if (typeof item === "number" || typeof item === "string") {
      return String(item).trim();
   }
   const raw =
      item.reservationId ??
      item.reservation_id ??
      item.reservation?.id ??
      item.id ??
      item._id;
   return raw != null ? String(raw).trim() : "";
}

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;

function parseYoutubeId(input) {
   const raw = String(input || "").trim();
   if (!raw) return "";
   if (YOUTUBE_ID_RE.test(raw)) return raw;

   let normalized = raw;
   if (!/^https?:\/\//i.test(normalized)) normalized = `https://${normalized}`;

   let url;
   try {
      url = new URL(normalized);
   } catch {
      return "";
   }

   const host = String(url.hostname || "")
      .replace(/^www\./i, "")
      .toLowerCase();

   if (host === "youtu.be") {
      const id = String(url.pathname || "")
         .split("/")
         .filter(Boolean)[0];
      return YOUTUBE_ID_RE.test(id) ? id : "";
   }

   const fromQuery = String(url.searchParams.get("v") || "").trim();
   if (YOUTUBE_ID_RE.test(fromQuery)) return fromQuery;

   const parts = String(url.pathname || "")
      .split("/")
      .filter(Boolean);
   const last = parts[parts.length - 1] || "";
   return YOUTUBE_ID_RE.test(last) ? last : "";
}

function watchUrl(youtubeId) {
   return `https://www.youtube.com/watch?v=${youtubeId}`;
}

function mapApiVideo(raw, index) {
   if (!raw || typeof raw !== "object") return null;
   const sourceUrl = raw.youtubeUrl || raw.url || raw.link || raw.youtube || "";
   const youtubeId = parseYoutubeId(sourceUrl);
   const id = raw.id ?? raw._id;
   if (id == null) return null;

   const fallbackName = `Material video ${String(index + 1).padStart(2, "0")}`;
   return {
      id,
      youtubeId,
      title: String(raw.title || raw.name || fallbackName).trim() || fallbackName,
      url: String(sourceUrl || (youtubeId ? watchUrl(youtubeId) : "")),
   };
}

function explainSendError(error) {
   const status = Number(error?.status || 0);
   const raw = String(error?.message || "").trim();

   if (status === 404) {
      return (
         "Lecția nu a fost găsită pentru trimitere. Poate aparține altui " +
         'instructor sau nu mai există. Trimite din „Orarul meu", pe o lecție proprie.'
      );
   }
   if (status === 400) {
      return "Videoclipul se poate trimite doar după ce lecția a avut loc (nu în avans, nici la lecții anulate).";
   }
   if (status === 409) {
      return "Ai trimis deja un videoclip pentru această lecție.";
   }
   return raw || "Nu s-a putut trimite videoclipul.";
}

function readStudentName(reservation) {
   if (!reservation || typeof reservation !== "object") return "";
   const first = String(
      reservation.firstName || reservation.userFirstName || "",
   ).trim();
   const last = String(
      reservation.lastName || reservation.userLastName || "",
   ).trim();
   const full = `${first} ${last}`.trim();
   return (
      full ||
      String(reservation.title || reservation.studentName || reservation.name || "").trim()
   );
}

export default function SendRecommendedVideoPopup({ reservationId, reservation }) {
   const [videos, setVideos] = useState([]);
   const [loading, setLoading] = useState(true);
   const [searchText, setSearchText] = useState("");
   const [sendingId, setSendingId] = useState("");
   const [sentId, setSentId] = useState("");
   const [alerts, setAlerts] = useState([]);
   const [eligibleIds, setEligibleIds] = useState(null);

   const aliveRef = useRef(true);
   useEffect(() => {
      aliveRef.current = true;
      return () => {
         aliveRef.current = false;
      };
   }, []);

   const pushAlert = (type, text) =>
      setAlerts((prev) => [
         ...prev,
         { id: Date.now() + Math.random(), type, text },
      ]);
   const dismissLastAlert = () => setAlerts((prev) => prev.slice(0, -1));

   useEffect(() => {
      if (!alerts.length) return;
      const tmo = setTimeout(dismissLastAlert, 4000);
      return () => clearTimeout(tmo);
   }, [alerts]);

   useEffect(() => {
      (async () => {
         setLoading(true);
         try {
            const res = await getRecommendedVideos({ page: 1, limit: 50 });
            if (!aliveRef.current) return;
            setVideos(
               (res.items || [])
                  .map((item, index) => mapApiVideo(item, index))
                  .filter(Boolean),
            );
         } catch (e) {
            if (aliveRef.current) {
               pushAlert(
                  "error",
                  e?.message || "Nu s-au putut încărca videoclipurile.",
               );
            }
         } finally {
            if (aliveRef.current) setLoading(false);
         }
      })();
   }, []);

   // Lecțiile pentru care backendul acceptă trimiterea (proprii, trecute,
   // neanulate, fără video încă). Le folosim ca să anunțăm din start dacă
   // lecția curentă nu e eligibilă (cauza reală a lui 404).
   useEffect(() => {
      (async () => {
         try {
            const pending = await getPendingVideoSends();
            if (!aliveRef.current) return;
            const ids = new Set(
               (Array.isArray(pending) ? pending : [])
                  .map(readPendingReservationId)
                  .filter(Boolean),
            );
            setEligibleIds(ids);
         } catch {
            if (aliveRef.current) setEligibleIds(null);
         }
      })();
   }, []);

   const studentName = useMemo(() => readStudentName(reservation), [reservation]);

   const notEligible = useMemo(() => {
      if (!eligibleIds || !reservationId) return false;
      return !eligibleIds.has(String(reservationId).trim());
   }, [eligibleIds, reservationId]);

   const filteredVideos = useMemo(() => {
      const query = String(searchText || "").trim().toLowerCase();
      if (!query) return videos;
      return videos.filter((video) =>
         `${video.title || ""} ${video.url || ""} ${video.youtubeId || ""}`
            .toLowerCase()
            .includes(query),
      );
   }, [videos, searchText]);

   const handleOpenVideo = (video) => {
      const href = String(video?.url || "").trim();
      if (!href || typeof window === "undefined") return;
      window.open(href, "_blank", "noopener,noreferrer");
   };

   const handleSend = useCallback(
      async (video) => {
         if (sendingId) return;
         if (!reservationId) {
            pushAlert("error", "Lecție invalidă.");
            return;
         }

         setSendingId(String(video.id));
         setAlerts([]);
         try {
            await sendRecommendedVideo({
               reservationId: Number(reservationId),
               videoId: Number(video.id),
            });
            if (!aliveRef.current) return;
            setSentId(String(video.id));
            pushAlert("success", "Videoclip trimis. Studentul a fost notificat.");
            setTimeout(() => {
               if (aliveRef.current) closePopup();
            }, 1000);
         } catch (e) {
            if (aliveRef.current) {
               pushAlert("error", explainSendError(e));
            }
         } finally {
            if (aliveRef.current) setSendingId("");
         }
      },
      [reservationId, sendingId],
   );

   return (
      <div className="studentVideoLibraryPopup">
         <AlertPills messages={alerts} onDismiss={dismissLastAlert} />

         <div className="studentVideoLibraryPopup__header">
            <h3 className="studentVideoLibraryPopup__title">Trimite videoclip</h3>
            {studentName ? (
               <p className="studentVideoLibraryPopup__meta">{studentName}</p>
            ) : null}
            {notEligible ? (
               <p className="studentVideoLibraryPopup__notice">
                  Această lecție nu apare printre cele disponibile pentru trimitere
                  (doar lecțiile proprii, deja desfășurate și fără videoclip trimis
                  pot primi un material). Trimiterea va fi respinsă de server.
               </p>
            ) : null}
         </div>

         <div className="studentVideoLibraryPopup__toolbar">
            <div className="studentVideoLibraryPopup__toolbarRow">
               <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Caută videoclip"
                  className="studentVideoLibraryPopup__search"
                  autoComplete="off"
               />
            </div>
         </div>

         <div className="studentVideoLibraryPopup__content">
            <section className="studentVideoLibraryPopup__list">
               {loading ? (
                  <div className="studentVideoLibraryPopup__emptyState">
                     <p className="studentVideoLibraryPopup__empty">Se încarcă...</p>
                  </div>
               ) : !filteredVideos.length ? (
                  <div className="studentVideoLibraryPopup__emptyState">
                     <p className="studentVideoLibraryPopup__empty">
                        Nu există videoclipuri. Adaugă întâi din „Videoclipuri".
                     </p>
                  </div>
               ) : (
                  <div className="studentVideoLibraryPopup__grid">
                     {filteredVideos.map((video) => (
                        <article
                           key={video.id}
                           className="studentVideoLibraryPopup__card"
                        >
                           <div
                              className="studentVideoLibraryPopup__thumb"
                              style={
                                 video.youtubeId
                                    ? {
                                         backgroundImage: `url(https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg)`,
                                      }
                                    : undefined
                              }
                           >
                              <p
                                 className="studentVideoLibraryPopup__cardTitle"
                                 title={video.title}
                              >
                                 {video.title}
                              </p>
                           </div>

                           <div className="studentVideoLibraryPopup__actions">
                              <button
                                 type="button"
                                 className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--ghost"
                                 onClick={() => handleOpenVideo(video)}
                                 disabled={!video.youtubeId}
                              >
                                 Vezi
                              </button>
                              <button
                                 type="button"
                                 className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--primary"
                                 onClick={() => handleSend(video)}
                                 disabled={
                                    !!sendingId || String(sentId) === String(video.id)
                                 }
                              >
                                 {String(sendingId) === String(video.id)
                                    ? "Se trimite..."
                                    : String(sentId) === String(video.id)
                                      ? "Trimis"
                                      : "Trimite"}
                              </button>
                           </div>
                        </article>
                     ))}
                  </div>
               )}
            </section>
         </div>
      </div>
   );
}
